import React, { useCallback, useEffect, useState } from "react";
import { Box, TextareaAutosize, Typography } from "@material-ui/core";
import {
  CustomButton,
  CustomCard,
  CustomDialog,
  CustomInput,
  DropzoneComponent,
  ReskinningAutocomplete,
  ReskinningDatePicker,
} from "../../../../../Components";

import useStyles from "./styles";
import { useSelectedTheme, useTranslate } from "../../../../../Hooks";
import { GetFileTypeIcon } from "../../../NewContactsView/ContactOverview/UI";
import { formatBytes } from "../../../../../Helper";
import { DeleteIcon } from "../../../../../assets/icons";
import {
  CreateInvoicesReceipt,
  lookupItemsGetId,
} from "../../../../../Services";
import { StaticLookupsIds } from "../../../../../assets/json/StaticLookupsIds";
import { useVerticalNav } from "../../../../../Contexts/VerticalNavContext";

const defaultFormValues = {
  bankId: null,
  branch: null,
  remarks: null,
  draftNo: null,
  chequeNo: null,
  netAmount: 0,
  invoicesIds: [],
  bondDetails: null,
  creditCardNo: null,
  paymentModeId: null,
  bankTransferNo: null,
  receiptContactTypeId: null,
  date: null,
  invoiceId: null,
};

function ReceiptDialog({ invoices, onClose = () => {}, refetch, open }) {
  const { translate } = useTranslate("AfterSalesClientProfileView");

  const {
    theme: { palette },
  } = useSelectedTheme();
  const [uploadedFileId, setUploadedFileId] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [acceptedFiles, setAcceptedFiles] = useState([]);

  const styles = useStyles();

  const handleFilesAccepted = (files) => {
    setAcceptedFiles(files);
  };
  const handleDeleteUploadedFile = () => {
    setUploadedFiles([]);
  };

  const [loadings, setLoadings] = React.useState({
    paymentModes: false,
    contactTypes: false,
    banks: false,
  });
  const { setAlertBoxContent } = useVerticalNav();
  const [paymentModes, setPaymentModes] = useState([]);
  const [contactTypes, setContactTypes] = useState([]);
  const [banks, setBanks] = useState([]);
  const [formValues, setFormValues] = useState(defaultFormValues);
  const remainingAmount = formValues.invoicesIds
    .filter((inv) => !inv?.invoiceStatus)
    .reduce((sum, invoice) => sum + invoice.amountDue, 0);
  const paidAmount = formValues.invoicesIds.reduce((sum, invoice) => {
    // sum the sub array first
    const subSum = invoice.receipts.reduce(
      (s, receipt) => s + receipt.netAmount,
      0
    );
    return sum + subSum;
  }, 0);
  const [error, setError] = useState({});
  const validateFields = () => {
    const errors = {};

    if (formValues.netAmount === "")
      errors.netAmount = "payment amount is required";
    if (!formValues.date) errors.date = "date is required";
    if (!formValues.paymentModeId)
      errors.paymentModeId = "payment mode is required";
    if (!formValues.invoicesIds.length === 0)
      errors.invoicesIds = "please select invoices";

    if (!formValues.receiptContactTypeId)
      errors.receiptContactTypeId = "contact type is required";
    if (!formValues.paymentModeId)
      errors.paymentModeId = "payment mode is required";
    if (formValues.netAmount > remainingAmount - paidAmount)
      errors.netAmount = "payment amount exceeds remaining amount";

    setError(errors);
    return Object.keys(errors).length > 0;
  };

  const getAllBanks = useCallback(async () => {
    setLoadings((items) => ({ ...items, banks: true }));
    const res = await lookupItemsGetId({
      lookupTypeId: StaticLookupsIds.Banks,
    });
    if (!(res && res.status && res.status !== 200)) setBanks(res || []);
    else setBanks([]);
    setLoadings((items) => ({ ...items, banks: false }));
  }, []);
  const getAllPaymentModes = useCallback(async () => {
    setLoadings((items) => ({ ...items, paymentModes: true }));
    const res = await lookupItemsGetId({
      lookupTypeId: StaticLookupsIds.PaymentMode,
    });
    if (!(res && res.status && res.status !== 200)) setPaymentModes(res || []);
    else setPaymentModes([]);
    setLoadings((items) => ({ ...items, paymentModes: false }));
  }, []);
  const getAllContactTypes = useCallback(async () => {
    setLoadings((items) => ({ ...items, contactTypes: true }));
    const res = await lookupItemsGetId({
      lookupTypeId: StaticLookupsIds.ContactTypes,
      lookupParentId: 430,
    });
    if (!(res && res.status && res.status !== 200)) setContactTypes(res || []);
    else setContactTypes([]);
    setLoadings((items) => ({ ...items, contactTypes: false }));
  }, []);

  useEffect(() => {
    if (open) {
      getAllPaymentModes();
      getAllContactTypes();
      getAllBanks();
    }
  }, [open]);

  const formatUploadedDocumentToMapp = () => {
    if (uploadedFileId && acceptedFiles.length > 0) {
      setUploadedFiles([
        {
          uuid: uploadedFileId,
          fileName: acceptedFiles[0]?.name,
          url: null,
          isUploaded: true,
          fileId: uploadedFileId,
        },
      ]);
    }
  };

  useEffect(() => {
    formatUploadedDocumentToMapp();
  }, [uploadedFileId]);

  useEffect(() => {
    validateFields();
  }, [formValues]);

  const showFields = (paymentModeId) => {
    switch (paymentModeId) {
      case 20695:
        return (
          <>
            <ReskinningAutocomplete
              withoutSearchButton
              data={banks}
              labelValue={"Bank"}
              displayLabel={(item) => item.lookupItemName}
              multiple={false}
              getOptionSelected={(item, value) =>
                item.lookupItemId === value.lookupItemId
              }
              // selectedValues={value}
              onChange={(_e, value) => {
                setFormValues((prev) => ({
                  ...prev,
                  bankId: value?.lookupItemId || "",
                }));
              }}
            />
            <Box className={styles.containerInputSection}>
              <Typography className={styles.titleFiled}>Branch</Typography>
              <CustomInput
                onChange={(event) => {
                  setFormValues((prev) => ({
                    ...prev,
                    branch: event.target?.value,
                  }));
                }}
                hasSearchIcon={false}
                CustomClass={styles.inputField}
              />
            </Box>
            <Box className={styles.containerInputSection}>
              <Typography className={styles.titleFiled}>
                Bank Transfer Number
              </Typography>
              <CustomInput
                onChange={(event) => {
                  setFormValues((prev) => ({
                    ...prev,
                    bankTransferNo: event.target?.value,
                  }));
                }}
                hasSearchIcon={false}
                CustomClass={styles.inputField}
              />
            </Box>
          </>
        );
      case 20696:
        return (
          <>
            <ReskinningAutocomplete
              withoutSearchButton
              data={banks}
              labelValue={"Bank"}
              displayLabel={(item) => item.lookupItemName}
              multiple={false}
              getOptionSelected={(item, value) =>
                item.lookupItemId === value.lookupItemId
              }
              // selectedValues={value}
              onChange={(_e, value) => {
                setFormValues((prev) => ({
                  ...prev,
                  bankId: value?.lookupItemId || "",
                }));
              }}
            />
            <Box className={styles.containerInputSection}>
              <Typography className={styles.titleFiled}>Branch</Typography>
              <CustomInput
                onChange={(event) => {
                  setFormValues((prev) => ({
                    ...prev,
                    branch: event.target?.value,
                  }));
                }}
                hasSearchIcon={false}
                CustomClass={styles.inputField}
              />
            </Box>
            <Box className={styles.containerInputSection}>
              <Typography className={styles.titleFiled}>
                Credit Card Number
              </Typography>
              <CustomInput
                onChange={(event) => {
                  setFormValues((prev) => ({
                    ...prev,
                    creditCardNo: event.target?.value,
                  }));
                }}
                hasSearchIcon={false}
                CustomClass={styles.inputField}
              />
            </Box>
          </>
        );
      case 20697:
        return (
          <>
            <ReskinningAutocomplete
              withoutSearchButton
              data={banks}
              labelValue={"Bank"}
              displayLabel={(item) => item.lookupItemName}
              multiple={false}
              getOptionSelected={(item, value) =>
                item.lookupItemId === value.lookupItemId
              }
              // selectedValues={value}
              onChange={(_e, value) => {
                setFormValues((prev) => ({
                  ...prev,
                  bankId: value?.lookupItemId || "",
                }));
              }}
            />
            <Box className={styles.containerInputSection}>
              <Typography className={styles.titleFiled}>Branch</Typography>
              <CustomInput
                onChange={(event) => {
                  setFormValues((prev) => ({
                    ...prev,
                    branch: event.target?.value,
                  }));
                }}
                hasSearchIcon={false}
                CustomClass={styles.inputField}
              />
            </Box>
            <Box className={styles.containerInputSection}>
              <Typography className={styles.titleFiled}>
                Draft Number
              </Typography>
              <CustomInput
                onChange={(event) => {
                  setFormValues((prev) => ({
                    ...prev,
                    draftNo: event.target?.value,
                  }));
                }}
                hasSearchIcon={false}
                CustomClass={styles.inputField}
              />
            </Box>
          </>
        );
      case 20694:
        return (
          <>
            <ReskinningAutocomplete
              withoutSearchButton
              data={banks}
              labelValue={"Bank"}
              displayLabel={(item) => item.lookupItemName}
              multiple={false}
              getOptionSelected={(item, value) =>
                item.lookupItemId === value.lookupItemId
              }
              onChange={(_e, value) => {
                setFormValues((prev) => ({
                  ...prev,
                  bankId: value?.lookupItemId || "",
                }));
              }}
            />
            <Box className={styles.containerInputSection}>
              <Typography className={styles.titleFiled}>Branch</Typography>
              <CustomInput
                onChange={(event) => {
                  setFormValues((prev) => ({
                    ...prev,
                    branch: event.target?.value,
                  }));
                }}
                hasSearchIcon={false}
                CustomClass={styles.inputField}
              />
            </Box>
            <Box className={styles.containerInputSection}>
              <Typography className={styles.titleFiled}>
                Cheque Number
              </Typography>
              <CustomInput
                onChange={(event) => {
                  setFormValues((prev) => ({
                    ...prev,
                    chequeNo: event.target?.value,
                  }));
                }}
                hasSearchIcon={false}
                CustomClass={styles.inputField}
              />
            </Box>
          </>
        );
      case 20693:
        return (
          <Box className={styles.containerInputSection}>
            <Typography className={styles.titleFiled}>
              Deposit Slip Number
            </Typography>
            <CustomInput
              onChange={(event) => {
                setFormValues((prev) => ({
                  ...prev,
                  depositSlipNo: event.target?.value,
                }));
              }}
              hasSearchIcon={false}
              CustomClass={styles.inputField}
            />
          </Box>
        );
      case 20698:
        return (
          <Box className={styles.containerInputSection}>
            <Typography className={styles.titleFiled}>
              Bond Deposit Details
            </Typography>
            <CustomInput
              onChange={(event) => {
                setFormValues((prev) => ({
                  ...prev,
                  bondDetails: event.target?.value,
                }));
              }}
              hasSearchIcon={false}
              CustomClass={styles.inputField}
            />
          </Box>
        );
      case 20699:
        return (
          <>
            <ReskinningAutocomplete
              withoutSearchButton
              data={banks}
              labelValue={"Bank"}
              displayLabel={(item) => item.lookupItemName}
              multiple={false}
              getOptionSelected={(item, value) =>
                item.lookupItemId === value.lookupItemId
              }
              onChange={(_e, value) => {
                setFormValues((prev) => ({
                  ...prev,
                  bankId: value?.lookupItemId || "",
                }));
              }}
            />
            <Box className={styles.containerInputSection}>
              <Typography className={styles.titleFiled}>Branch</Typography>
              <CustomInput
                onChange={(event) => {
                  setFormValues((prev) => ({
                    ...prev,
                    branch: event.target?.value,
                  }));
                }}
                hasSearchIcon={false}
                CustomClass={styles.inputField}
              />
            </Box>
            <Box className={styles.containerInputSection}>
              <Typography className={styles.titleFiled}>
                Bank Deposit Number
              </Typography>
              <CustomInput
                onChange={(event) => {
                  setFormValues((prev) => ({
                    ...prev,
                    depositNo: event.target?.value,
                  }));
                }}
                hasSearchIcon={false}
                CustomClass={styles.inputField}
              />
            </Box>
          </>
        );
      default:
        return null;
    }
  };

  const saveHandler = useCallback(async () => {
    const res = await CreateInvoicesReceipt({
      ...formValues,
      invoicesIds: formValues.invoicesIds.map((invoice) => invoice.invoiceId),
      receiptDocuments: uploadedFiles,
      files: uploadedFiles,
      remarks: formValues.remarks || " ",
    });
    if (!(res?.status && res.status !== 200)) {
      refetch();
      return setAlertBoxContent({
        display: true,
        variant: "success",
        title: translate("receipt-created-successfully"),
        onClose: () => {
          setAlertBoxContent(null);
        },
      });
    }
    const errorMessage = res?.data?.Message
      ? res?.data.Message.split(" : ")[1]
      : "";
    let error = translate("error-creating-receipt");
    if (errorMessage === "CANNOT_CREATE_OR_UPDATE_RECEIPT_FOR_PAID_INVOICE") {
      error = translate("cannot-create-paid-invoice");
    } else if (errorMessage === "PAYMENT_AMOUNT_EXCEEDS_REMAINING_BALANCE") {
      error = translate("exceeds-remaining-balance");
    }
    return setAlertBoxContent({
      display: true,
      variant: "error",
      title: error,
      onClose: () => {
        setAlertBoxContent(null);
      },
    });
  }, [formValues, uploadedFiles]);
  return (
    <CustomDialog
      open={open}
      onClose={() => {
        onClose();
        setFormValues(defaultFormValues);
      }}
      title={"Add Receipt"}
      paperClassName={styles.dialogWrapper}
      showActions={false}
      content={
        <Box>
          <Box className={styles.bannerBox}>
            <Typography
              style={{
                fontWeight: "600",
                color: palette.text.brand_secondary,
              }}
            >
              Recipt Number {"<Auto>"}
            </Typography>
            <Typography
              style={{
                fontWeight: "600",
                color: palette.text.brand_secondary,
              }}
            >
              Remaining: {remainingAmount - paidAmount} AED
            </Typography>
          </Box>
          <Box className={styles.fieldsSection}>
            <ReskinningAutocomplete
              withoutSearchButton
              data={
                invoices?.filter(
                  (invoice) =>
                    invoice.invoiceStateName?.toLowerCase() !== "paid"
                ) || []
              }
              labelValue={"Invoice Number *"}
              displayLabel={(item) => item?.invoiceId}
              multiple={true}
              chipsLabel={(item) => item?.invoiceId}
              getOptionSelected={(item, value) => {
                return item?.invoiceId === value?.invoiceId;
              }}
              onChange={(_e, value) => {
                setFormValues((prev) => ({
                  ...prev,
                  invoicesIds: value || "",
                }));
              }}
              filterOptions={(options, state) => options}
              error={error.invoiceContactTypeId}
              helperText={error.invoiceContactTypeId}
              isWithError={!!error.invoiceContactTypeId}
            />
            <ReskinningAutocomplete
              withoutSearchButton
              data={contactTypes}
              labelValue={"Contact Type *"}
              displayLabel={(item) => item.lookupItemName}
              multiple={false}
              getOptionSelected={(item, value) =>
                item.lookupItemId === value.lookupItemId
              }
              onChange={(_e, value) => {
                setFormValues((prev) => ({
                  ...prev,
                  receiptContactTypeId: value?.lookupItemId || "",
                }));
              }}
              error={error.receiptContactTypeId}
              helperText={error.receiptContactTypeId}
              isWithError={!!error.receiptContactTypeId}
              isDisabled={formValues.invoicesIds.length === 0}
            />

            <ReskinningAutocomplete
              withoutSearchButton
              data={paymentModes}
              labelValue={"Payment Mode *"}
              displayLabel={(item) => item.lookupItemName}
              multiple={false}
              getOptionSelected={(item, value) =>
                item.lookupItemId === value.lookupItemId
              }
              onChange={(_e, value) => {
                setFormValues((prev) => ({
                  ...prev,
                  paymentModeId: value?.lookupItemId || "",
                }));
              }}
              error={error.paymentModeId}
              helperText={error.paymentModeId}
              isWithError={!!error.paymentModeId}
              isDisabled={formValues.invoicesIds.length === 0}
            />
            <Box className={styles.containerInputSection}>
              <Typography className={styles.titleFiled}>
                Net Amount *
              </Typography>
              <CustomInput
                onChange={(event) => {
                  setFormValues((prev) => ({
                    ...prev,
                    netAmount: event.target?.value,
                  }));
                }}
                value={formValues?.netAmount}
                hasSearchIcon={false}
                hasError={!!error.netAmount}
                errorMessage={error.netAmount}
                submitted={!!error.netAmount}
                CustomClass={styles.inputField}
                type="number"
                disabled={formValues.invoicesIds.length === 0}
              />
            </Box>
            <ReskinningDatePicker
              labelValue="Date *"
              onDateChanged={(date) => {
                setFormValues((prev) => ({
                  ...prev,
                  date: date,
                }));
              }}
              value={formValues?.date || null}
              error={error.date}
              helperText={error.date}
              isWithError={!!error.date}
              isDisabled={formValues.invoicesIds.length === 0}
            />
            <Box className={styles.containerInputSection}>
              <Typography className={styles.titleFiled}>Remarks</Typography>
              <Box className={styles.wrapperTextArea}>
                <TextareaAutosize
                  minRows={4}
                  onChange={(event) => {
                    // Try accessing value directly from the event
                    const value =
                      event && event.target
                        ? event.target.value
                        : event && event.currentTarget
                        ? event.currentTarget.value
                        : event;
                    setFormValues((prev) => ({
                      ...prev,
                      remarks: value,
                    }));
                  }}
                  disabled={formValues.invoicesIds.length === 0}
                  className={styles.textAreaInput}
                />
              </Box>
            </Box>

            {showFields(formValues?.paymentModeId)}
            <Box className={styles.containerInputSection}>
              <Typography className={styles.titleFiled}>
                {translate("attachment")}
              </Typography>
              <Box className={styles.dropzoneSection}>
                <DropzoneComponent
                  setUploadedFileId={setUploadedFileId}
                  accept=".xls,.xlsx,.xlsm,.pdf,.doc,.docx,.jpg,.jpeg,.png,"
                  title={translate("SVG-PNG-JPG-or-GIF-max-800x400px")}
                  onFilesAccepted={handleFilesAccepted}
                />
                {uploadedFiles && uploadedFiles?.length > 0 && (
                  <Box className={styles.containerUploadedFilesCards}>
                    <CustomCard
                      boxShadow="xs"
                      borderRadius="xl"
                      borderColor="secondary"
                      classes={styles.CardUploadedFilesInfo}
                    >
                      {GetFileTypeIcon(uploadedFiles[0]?.fileType)}
                      <Box className={styles.ContainerFileUploadedInfo}>
                        <Box className={styles.containerTitleAndDeleteIcon}>
                          <Typography className={styles.TitleFileName}>
                            {uploadedFiles[0]?.fileName}
                          </Typography>
                          <DeleteIcon
                            width="20"
                            height="20"
                            className={styles.DeleteIcon}
                            style={{ cursor: "pointer" }}
                            fill={palette.button.secondary_fg}
                            onClick={handleDeleteUploadedFile}
                          />
                        </Box>
                        <Typography className={styles.FileSizeTitle}>
                          {formatBytes(acceptedFiles[0]?.size)}
                        </Typography>
                      </Box>
                    </CustomCard>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
          <Box className={styles.footerActions}>
            <CustomButton
              variant="outlined"
              color="secondary"
              onClick={onClose}
            >
              {translate("cancel")}
            </CustomButton>
            <CustomButton
              type="submit"
              variant="contained"
              color="primary"
              disabled={Object.keys(error).length > 0}
              onClick={() => {
                const isWithError = validateFields();
                if (!isWithError) {
                  saveHandler();
                  onClose();
                  setFormValues(defaultFormValues);
                } else {
                  setAlertBoxContent({
                    display: true,
                    variant: "error",
                    title: translate("please fill required fields"),
                    onClose: () => {
                      setAlertBoxContent(null);
                    },
                  });
                }
              }}
            >
              {translate("save")}
            </CustomButton>
          </Box>
        </Box>
      }
    />
  );
}

export default ReceiptDialog;
